import { useTheme } from '@mui/material';

import { useTrans } from '@libs/i18n';

import type { NoFlipMode } from '../utils/createNoFlip';

export interface DirectionState {
  /** Resolved app text direction — feed straight into a NoFlip `mode`. */
  dir: NoFlipMode;
  /** Convenience flag for layout decisions (icon mirroring, anchor origins). */
  isRtl: boolean;
}

/**
 * The one place the app answers "are we in RTL right now?".
 *
 * It replaces the three variants that used to be spread across pages —
 * `i18n.dir() === 'rtl'`, `theme.direction === 'rtl'` and
 * `i18n.language.startsWith('ar')` — which disagree for a render or two while a
 * language switch propagates from i18next to the MUI theme.
 *
 * Either source reporting RTL wins, so a subtree never renders LTR-pinned text
 * inside an already-flipped stylis cache during that window.
 */
export const useDirection = (): DirectionState => {
  const { i18n } = useTrans();
  const theme = useTheme();

  const isRtl = i18n.dir() === 'rtl' || theme.direction === 'rtl';

  return { dir: isRtl ? 'rtl' : 'ltr', isRtl };
};
